import { Form } from "@inertiajs/react";
import {
  Box,
  Button,
  Callout,
  Flex,
  Grid,
  Heading,
  Link,
  Select,
  Separator,
  Switch,
  Text,
  TextArea,
  TextField,
} from "@radix-ui/themes";
import { AlertCircle, Mail, MapPinHouse, Phone, User } from "lucide-react";
import { SubmitEvent } from "react";

export interface StaffFormData {
  participant: {
    name: string;
    location: string;
    email: string;
    phone_number: string;
    instagram: string;
    youtube: string;
    flickr: string;
  };
  staff: {
    events_worked: string;
    cashier_experience: string;
    favored_positions: string;
    days_staffed: number;
    staffed_before: boolean;
    cashiered_before: boolean;
  };
}

interface StaffFormProps {
  data: StaffFormData;
  setData: <K extends keyof StaffFormData>(key: K, value: StaffFormData[K]) => void
  onSubmit: (e: SubmitEvent) => void;
  processing: boolean
  errors?: string[];
  title: string;
  /** Sits under the title. Defaults to the first-time setup pitch. */
  subtitle?: string;
  submitLabel: string;
  cancelPath?: string;
}

export default function StaffForm({
  data,
  setData,
  onSubmit,
  processing,
  errors,
  title,
  subtitle = "Fill out this form to make applying for staff positions at events super fast.",
  submitLabel,
  cancelPath = '/profile',
}: StaffFormProps) {
  return (
    <Box className="border-2 py-8 px-10">
      <Heading size="7">{title}</Heading>
      <Text size="4" as="p" color="gray">
        {subtitle}
      </Text>
      <Separator my="3" size="4" />
      {errors && errors.length > 0 && (
        <Callout.Root color="red" role="alert">
          {errors.map((error, index) => (
            <div key={index}>
              <Callout.Icon>
                <AlertCircle />
              </Callout.Icon>
              <Callout.Text>{error}</Callout.Text>
            </div>
          ))}
        </Callout.Root>
      )}
          <form onSubmit={onSubmit}>
            <Grid columns="1" gap="5" my="5">
              <Box>
                <Text as="label" size="3">
                  <Flex gap="2" direction="column">
                    <Text size="5">Name *</Text>
                    <TextField.Root
                      id="name"
                      name="participant.name"
                      size="3"
                      onChange={(e) => setData("participant", { ...data.participant, name: e.target.value })}
                      value={data.participant.name}
                      required
                      placeholder="Full name"
                    >
                      <TextField.Slot>
                        <User height="16" width="16" />
                      </TextField.Slot>
                    </TextField.Root>
                    
                  </Flex>
                </Text>
              </Box>
              <Box>
                <Text as="label" size="3">
                  <Flex gap="2" direction="column">
                    <Text size="5">Location</Text>
                    <TextField.Root
                      id="location"
                      size="3"
                      name="participant.location"
                      value={data.participant.location}
                      onChange={(e) => setData("participant", { ...data.participant, location: e.target.value })}
                      
                      placeholder="Where are you (ish?)"
                    >
                      <TextField.Slot>
                        <MapPinHouse height="16" width="16" />
                      </TextField.Slot>
                    </TextField.Root>
                    
                  </Flex>
                </Text>
              </Box>
              <Box>
                <Text as="label" size="3">
                  <Flex gap="2" direction="column">
                    <Text size="5">Email</Text>
                    <TextField.Root
                      type="email"
                      size="3"
                      id="email"
                      value={data.participant.email}
                      onChange={(e) => setData("participant", { ...data.participant, email: e.target.value })}
                      
                      name="participant.email"
                      placeholder="Better email to reach you at than your primary?"
                    >
                      <TextField.Slot>
                        <Mail height="16" width="16" />
                      </TextField.Slot>
                    </TextField.Root>
                    
                  </Flex>
                </Text>
              </Box>
              <Box>
                <Text as="label" size="3">
                  <Flex gap="2" direction="column">
                    <Text size="5">Phone Number</Text>
                    <TextField.Root
                      type="text"
                      size="3"
                      id="phone_number"
                      value={data.participant.phone_number}
                      onChange={(e) => setData("participant", { ...data.participant, phone_number: e.target.value })}
                      
                      name="participant.phone_number"
                      placeholder="Sometimes it's easier to just call you"
                    >
                      <TextField.Slot>
                        <Phone height="16" width="16" />
                      </TextField.Slot>
                    </TextField.Root>
                  </Flex>
                  
                    
                </Text>
              </Box>
            </Grid>

            <Text size="6">Staffing History</Text>
            <Separator my="3" size="4" />
            <Grid columns={{ initial: '1', sm: '2' }} gap="5" my="5">
              <Box>
                <Text as="label" size="3">
                  <Flex gap="2" direction="column">
                    <Text size="5">Events Worked</Text>
                    <Select.Root
                      defaultValue="0"
                      size="3"
                      value={data.staff.events_worked}
                      name="staff.events_worked"
                      onValueChange={(v) => setData("staff", { ...data.staff, events_worked: v})}
                    >
                      <Select.Trigger id="events_worked" />
                      <Select.Content>
                        <Select.Item value="0">None</Select.Item>
                        <Select.Item value="1">1 Day</Select.Item>
                        <Select.Item value="2-5">2 - 5 Days</Select.Item>
                        <Select.Item value=">5">More than 5 days</Select.Item>
                      </Select.Content>
                      
                    
                    </Select.Root>
                  </Flex>
                </Text>
              </Box>
              <Box>
                <Text as="label" size="3">
                  <Flex gap="2" direction="column">
                    <Text size="5">Preferred Position</Text>
                    <Select.Root
                      size="3"
                      name="staff.favored_positions"
                      value={data.staff.favored_positions}
                      onValueChange={(v) => setData("staff", { ...data.staff, favored_positions: v})}
                      defaultValue="any"
                    >
                      <Select.Trigger id="favored_positions" />
                      <Select.Content>
                        <Select.Item value="any">Any Position</Select.Item>
                        <Select.Item value="track">Track Staff</Select.Item>
                        <Select.Item value="booth">Booth Staff</Select.Item>
                      </Select.Content>
                    </Select.Root>
                  
                    
                  </Flex>
                  
                </Text>
              </Box>
              <Box py="3">
                <Text as="label" size="3">
                  <Flex gap="2" direction="row">
                    <Switch
                      color="indigo"
                      size="3"
                      
                      checked={data.staff.cashiered_before}
                      onCheckedChange={(e) => setData("staff", {...data.staff, cashiered_before: e})}
                      
                      name="staff.cashiered_before"

                    />{" "}
                    <Text size="5">Cashiered Before</Text>
                    
                  </Flex>
                </Text>
              </Box>
              <Box py="3">
                <Text as="label" size="3">
                  <Flex gap="2" direction="row">
                    <Switch
                      size="3"
                      color="indigo"
                      name="staff.staffed_before"
                      checked={data.staff.staffed_before}
                      onCheckedChange={(e) => setData("staff", {...data.staff, staffed_before: e})}
                      
                    />{" "}
                    <Text size="5">Staffed Before</Text>
                  </Flex>
                  
                </Text>
              </Box>
              <Box>
                <Text as="label" size="3">
                  <Flex gap="2" direction="column">
                    <Text size="5">Cashier Experience</Text>
                    <TextArea
                      id="cashier_experience"
                      size="3"
                      value={data.staff.cashier_experience}
                      name="staff.cashier_experience"
                      rows={5}
                      placeholder="Where have you worked as a cashier before?"
                      onChange={(e) => setData("staff", { ...data.staff, cashier_experience: e.target.value })}
                      
                    />
                    
                  </Flex>
                </Text>
              </Box>
            </Grid>

            <Box mt="8">
              <Flex gap="4" justify="end" dir="row">
                <Button
                  type="button"
                  size="3"
                  asChild
                  color="gray"
                  variant="soft"
                >
                  <Link href={cancelPath}>Cancel</Link>
                </Button>
                <Button size="3" type="submit" disabled={processing}>
                  {submitLabel}
                </Button>
              </Flex>
            </Box>
          </form>
    </Box>
  );
}
